// src/sections/gamification/CelebrationOverlay.jsx
// Full-screen celebration takeover on portal open (integration retro-fit,
// owner directive 2026-07-05: completions must be a MOMENT, not a banner
// buried on the Missions page). Mounted once in the authed dashboard layout;
// fully self-gating. Sources: the viewer's missionProgress doc (mission +
// track completions) and referral activations, compared against a
// localStorage seen-set — the same `missionsSeen_{seasonId}_{userDocId}` key
// the Missions page used before this component took the job over, so nobody
// gets re-celebrated for old wins. Seen-markers are persisted on DISMISS so a
// missed render retries next open.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { Hero, NIGHT, nightStage, Atmosphere, GoldCTA } from './plays/uiKit';
import { particleBurst } from './plays/confetti';

import { getDoc, getDocsFromServer, query, where, limit } from 'firebase/firestore';
import { eventCollection, eventDoc } from '../../event/paths';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { EventContext } from '../../event/eventContext';
import { useContext } from 'react';
import useGamificationConfig from '../../hooks/useGamificationConfig';
import { seasonWindowFor, weekKeyForDay, todayIsoDay } from '../../utils/gamificationSeason';
import { money } from '../../utils/gamificationPerks';
import { resolveLevel } from '../../utils/gamificationGame';
import { downloadShareCard } from '../../utils/shareCard';
import WheelSpinModal from './WheelSpinModal';

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — celebration just repeats next visit */
  }
}

// Tier trophy renders (owner assets): matched by level NAME so any ladder
// whose levels are called Bronze/Silver/Gold/Platinum/Legend gets its trophy;
// unmatched names fall back to the trophy icon.
const TROPHY_TIERS = ['legend', 'platinum', 'gold', 'silver', 'bronze'];
function trophySlot(levelName) {
  const n = String(levelName || '').toLowerCase();
  const tier = TROPHY_TIERS.find((t) => n.includes(t));
  return tier ? `trophy-${tier}` : null;
}

// In-palette canvas confetti burst fired once when the dialog opens.
function CelebrationBurst() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const stop = particleBurst(ref.current, {
      x: ref.current.clientWidth / 2,
      y: ref.current.clientHeight * 0.3,
      count: 140,
      power: 9
    });
    return stop;
  }, []);
  return (
    <Box
      component="canvas"
      ref={ref}
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      aria-hidden
    />
  );
}

export default function CelebrationOverlay() {
  const { profile } = useAuth() || {};
  const eventCtx = useContext(EventContext);
  const { config, loading: configLoading } = useGamificationConfig();
  const [celebrations, setCelebrations] = useState([]);
  const [seenWrites, setSeenWrites] = useState([]);
  const [open, setOpen] = useState(false);
  const [wheelSpin, setWheelSpin] = useState(null); // { levelId, slices, winningIndex, levelName, markSeen }
  const navigate = useNavigate();

  const missions = config.missions;
  const userDocId = profile?.id || null;
  const userUID = profile?.userUID || null;

  useEffect(() => {
    if (configLoading || !missions.enabled || !userDocId || !userUID) return undefined;
    const window_ = seasonWindowFor(missions);
    if (!window_) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [progressSnap, referralSnap, gameProfileSnap, achievementsSnap, questWeekSnap] = await Promise.all([
          getDocsFromServer(
            query(
              eventCollection(db, 'missionProgress'),
              where('userUID', '==', userUID),
              where('seasonId', '==', window_.seasonId),
              limit(1)
            )
          ).catch(() => null),
          config.referral.enabled
            ? getDocsFromServer(query(eventCollection(db, 'referrals'), where('recruiterUID', '==', userUID))).catch(() => null)
            : null,
          config.game.enabled ? getDoc(eventDoc(db, 'gameProfiles', userDocId)).catch(() => null) : null,
          config.game.enabled
            ? getDocsFromServer(query(eventCollection(db, 'achievements'), where('userUID', '==', userUID))).catch(() => null)
            : null,
          config.game.enabled && config.quests.enabled
            ? getDoc(eventDoc(db, 'questWeeks', `${weekKeyForDay(todayIsoDay())}_${userDocId}`)).catch(() => null)
            : null
        ]);
        if (cancelled) return;

        const events = [];
        const writes = [];
        let wheelTakeover = false;

        const pDoc = progressSnap && !progressSnap.empty ? progressSnap.docs[0].data() : null;
        if (pDoc) {
          const missionsSeenKey = `missionsSeen_${window_.seasonId}_${userDocId}`;
          const tracksSeenKey = `tracksSeen_${window_.seasonId}_${userDocId}`;
          const completedIds = Object.entries(pDoc.missions || {})
            .filter(([, m]) => m && m.completedAt)
            .map(([id]) => id)
            .sort();
          const finishedTrackIds = Object.entries(pDoc.tracks || {})
            .filter(([, t]) => t && t.finishedAt)
            .map(([id]) => id)
            .sort();
          const seenMissions = readJson(missionsSeenKey, []);
          const seenTracks = readJson(tracksSeenKey, []);

          for (const id of completedIds.filter((x) => !seenMissions.includes(x))) {
            const def = missions.catalog.find((m) => m.id === id);
            events.push({
              key: `mission_${id}`,
              kicker: 'MISSION COMPLETE',
              headline: def?.title || id,
              title: def?.title || id,
              detail:
                def?.rewardType === 'cash' && Number(def.rewardAmount) > 0
                  ? `${money(def.rewardAmount)} reward`
                  : def?.perk
                    ? `${def.perk.label || 'Perk'} earned`
                    : 'Mission complete'
            });
          }
          for (const id of finishedTrackIds.filter((x) => !seenTracks.includes(x))) {
            const def = missions.tracks.find((t) => t.id === id);
            events.push({
              key: `track_${id}`,
              kicker: 'TRACK FINISHED',
              headline: def?.title || id,
              title: `${def?.title || id} — track finished!`,
              detail: Number(def?.finisherRewardCash) > 0 ? `${money(def.finisherRewardCash)} finisher bonus` : 'Every mission done'
            });
          }
          writes.push({ key: missionsSeenKey, value: completedIds });
          writes.push({ key: tracksSeenKey, value: finishedTrackIds });
        }

        if (referralSnap) {
          const referralsSeenKey = `referralsSeen_${userDocId}`;
          const seenCount = Number(readJson(referralsSeenKey, 0)) || 0;
          const count = referralSnap.size;
          if (count > seenCount) {
            events.push({
              key: 'referrals',
              title: `${count - seenCount} recruit${count - seenCount === 1 ? '' : 's'} activated!`,
              detail: `Your recruiter bonus is in your wallet`
            });
          }
          writes.push({ key: referralsSeenKey, value: count });
        }

        // Phase 3: level-ups + newly earned badges (shareable moments).
        if (gameProfileSnap && gameProfileSnap.exists()) {
          const gp = gameProfileSnap.data() || {};
          const level = resolveLevel(gp.lifetimeXp, config.game.levels);
          const levelSeenKey = `levelSeen_${userDocId}`;
          // Baseline is { id, index } — matching the engine's leveledUp rule:
          // celebrate only an id CHANGE with upward index movement. Index
          // alone false-fires when an admin inserts a ladder rung below the
          // host (index shifts, level doesn't — Codex review 2026-07-05).
          const seenRaw = readJson(levelSeenKey, null);
          const seen = seenRaw && typeof seenRaw === 'object' && typeof seenRaw.id === 'string' ? seenRaw : null;
          // First sight (or a pre-object legacy value) records silently —
          // landing on Bronze from endowed XP is a head start, not an
          // achievement.
          if (seen && level.index > Number(seen.index) && level.id !== seen.id) {
            events.push({
              key: `level_${level.id}`,
              levelName: level.name,
              title: `You reached ${level.name}!`,
              detail: `${Math.max(0, Number(gp.lifetimeXp) || 0)} XP`,
              share: { headline: `${level.name} unlocked`, subline: `${Math.max(0, Number(gp.lifetimeXp) || 0)} XP and climbing` }
            });
          }
          writes.push({ key: levelSeenKey, value: { id: level.id, index: level.index } });
        }
        // Phase 4: wheel spins — the first unseen SETTLED spin gets the full
        // animated takeover (WheelSpinModal, chained ahead of this dialog); a
        // spin whose slice no longer exists in config falls back to a plain
        // entry. The modal writes its own seen-marker on close; additional
        // unseen spins surface on the next portal open.
        if (gameProfileSnap && gameProfileSnap.exists()) {
          const spins = (gameProfileSnap.data() || {}).wheelSpins || {};
          const wheelSeenKey = `wheelSeen_${userDocId}`;
          const seenSpins = readJson(wheelSeenKey, []);
          const unseenSettled = Object.keys(spins)
            .sort()
            .filter((l) => !seenSpins.includes(l))
            .filter((l) => (spins[l] || {}).rewardStatus !== 'pending' && (spins[l] || {}).rewardStatus !== 'held');
          if (unseenSettled.length) {
            const levelId = unseenSettled[0];
            const spin = spins[levelId] || {};
            const slices = config.wheel.slices || [];
            const winningIndex = slices.findIndex((s) => s.type === spin.type && (s.label || s.type) === (spin.label || spin.type));
            if (winningIndex >= 0) {
              const levelDef = config.game.levels.find((l) => l.id === levelId);
              wheelTakeover = true;
              setWheelSpin({
                levelId,
                slices,
                winningIndex,
                levelName: levelDef ? levelDef.name : levelId,
                markSeen: () => writeJson(wheelSeenKey, [...seenSpins, levelId])
              });
            } else {
              events.push({
                key: `wheel_${levelId}`,
                kicker: 'WHEEL PRIZE',
                headline: spin.type === 'nothing' ? 'No prize this time' : spin.label || spin.type,
                title: spin.type === 'nothing' ? 'You spun the level-up wheel!' : `Wheel prize: ${spin.label || spin.type}! 🎡`,
                detail: spin.type === 'nothing' ? 'No prize this time — next level, next spin' : 'Level-up wheel',
                share: spin.type === 'nothing' ? undefined : { headline: `${spin.label || 'Wheel prize'}`, subline: 'Level-up wheel win' }
              });
              writes.push({ key: wheelSeenKey, value: [...seenSpins, levelId] });
            }
          }
        }
        if (questWeekSnap && questWeekSnap.exists()) {
          const weekKey = weekKeyForDay(todayIsoDay());
          const questsSeenKey = `questsSeen_${weekKey}_${userDocId}`;
          const quests = (questWeekSnap.data() || {}).quests || {};
          const completedQuests = Object.entries(quests)
            .filter(([, q]) => q && q.completedAt)
            .map(([id]) => id)
            .sort();
          const seenQuests = readJson(questsSeenKey, []);
          for (const id of completedQuests.filter((x) => !seenQuests.includes(x))) {
            const q = quests[id] || {};
            events.push({
              key: `quest_${id}`,
              kicker: 'QUEST COMPLETE',
              headline: q.title || id,
              title: `Quest complete: ${q.title || id} ⚡`,
              detail:
                q.reward?.type === 'cash'
                  ? `${money(q.reward.amount)} reward`
                  : q.reward?.type === 'perk'
                    ? q.reward.perk?.label || 'Perk earned'
                    : q.reward?.type === 'xp'
                      ? `+${q.reward.xp} XP`
                      : 'Done'
            });
          }
          writes.push({ key: questsSeenKey, value: completedQuests });
        }

        if (achievementsSnap) {
          const badgesSeenKey = `badgesSeen_${userDocId}`;
          const owned = achievementsSnap.docs
            .map((d) => (d.data() || {}).badgeId)
            .filter(Boolean)
            .sort();
          const seenBadges = readJson(badgesSeenKey, []);
          for (const badgeId of owned.filter((b) => !seenBadges.includes(b))) {
            const a = achievementsSnap.docs.map((d) => d.data() || {}).find((x) => x.badgeId === badgeId) || {};
            events.push({
              key: `badge_${badgeId}`,
              kicker: 'BADGE EARNED',
              headline: a.title || badgeId,
              title: `Badge earned: ${a.title || badgeId} ${a.icon || '🏅'}`,
              detail: Number(a.xpBonus) > 0 ? `+${a.xpBonus} XP` : 'One-off trophy',
              share: { headline: a.title || badgeId, subline: 'Badge earned' }
            });
          }
          writes.push({ key: badgesSeenKey, value: owned });
        }

        if (events.length) {
          setCelebrations(events);
          setSeenWrites(writes);
          // The wheel takeover plays FIRST; the generic dialog opens when it
          // closes (see closeWheel).
          if (!wheelTakeover) setOpen(true);
        } else {
          // Nothing to celebrate: persist the silent baselines NOW. They only
          // flush on dismiss otherwise, and with no modal there is no dismiss
          // — the level first-sight baseline would never be stored and every
          // future level-up would be suppressed against a permanent "unseen"
          // state (Codex review 2026-07-05).
          for (const w of writes) writeJson(w.key, w.value);
        }
      } catch (err) {
        console.error('[celebrations] load failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configLoading,
    missions.enabled,
    missions.seasonStart,
    userDocId,
    userUID,
    config.referral.enabled,
    config.game.enabled,
    config.quests.enabled
  ]);

  const dismiss = (thenNavigate) => {
    for (const w of seenWrites) writeJson(w.key, w.value);
    setOpen(false);
    if (thenNavigate) navigate('/missions');
  };

  const shareable = celebrations.find((c) => c.share);
  const brandName = eventCtx?.brand?.name || 'Club Astra';
  const hostFirstName =
    String(profile?.name || '')
      .trim()
      .split(/\s+/)[0] || '';

  // Wheel takeover plays before anything else; the generic dialog (if any
  // other celebrations queued) opens when it closes.
  if (wheelSpin) {
    return (
      <WheelSpinModal
        slices={wheelSpin.slices}
        winningIndex={wheelSpin.winningIndex}
        levelName={wheelSpin.levelName}
        brandName={brandName}
        hostFirstName={hostFirstName}
        onClose={() => {
          wheelSpin.markSeen();
          setWheelSpin(null);
          if (celebrations.length) setOpen(true);
        }}
      />
    );
  }

  if (!open) return null;

  // A level-up is the hero moment: tier trophy render + big headline; every
  // other celebration lists as a capsule row beneath. Without a level-up the
  // first event's title takes the headline slot.
  const levelEvent = celebrations.find((c) => c.key.startsWith('level_'));
  const trophy = levelEvent ? trophySlot(levelEvent.levelName) : null;
  const others = celebrations.filter((c) => c !== levelEvent).slice(0, 4);

  return (
    <Dialog
      open
      onClose={() => dismiss(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', background: nightStage, color: NIGHT.white } }}
    >
      <Box sx={{ position: 'relative', px: 3, pt: 4, pb: 3.5, textAlign: 'center' }}>
        <Atmosphere />
        <CelebrationBurst />
        <Box sx={{ position: 'relative' }}>
          {/* spotlight pool behind the hero */}
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,198,68,0.22) 0%, transparent 62%)',
              filter: 'blur(6px)',
              pointerEvents: 'none'
            }}
          />
          {levelEvent ? (
            <Box
              sx={{
                display: 'inline-flex',
                animation: 'trophyRise 0.7s cubic-bezier(0.16, 1.3, 0.36, 1) both',
                '@keyframes trophyRise': {
                  from: { transform: 'translateY(36px) scale(0.8)', opacity: 0 },
                  to: { transform: 'none', opacity: 1 }
                }
              }}
            >
              <Hero
                asset={trophy || 'trophy-gold'}
                size={190}
                glow="rgba(245,198,68,0.35)"
                fallback={<EmojiEventsIcon sx={{ fontSize: 120, color: NIGHT.gold }} />}
              />
            </Box>
          ) : (
            <EmojiEventsIcon sx={{ fontSize: 64, color: NIGHT.gold, filter: 'drop-shadow(0 0 18px rgba(245,198,68,0.5))' }} />
          )}
          <Typography
            sx={{
              mt: levelEvent ? 1.5 : 1,
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: '0.42em',
              textIndent: '0.42em',
              color: NIGHT.violetSoft,
              textShadow: '0 0 16px rgba(139,92,246,0.6)'
            }}
          >
            {levelEvent ? 'LEVEL UP' : celebrations[0]?.kicker || (celebrations.length === 1 ? 'NICE WORK' : 'ON FIRE TONIGHT')}
          </Typography>
          {/* level-ups get the huge trophy wordmark; everything else uses a
              calmer size so long quest/mission titles wrap like a headline,
              not a wall (owner mobile pass 2026-07-06) */}
          <Typography
            sx={{
              mt: 0.25,
              px: 1,
              fontWeight: 900,
              fontSize: levelEvent ? { xs: 34, sm: 40 } : { xs: 22, sm: 26 },
              letterSpacing: levelEvent ? '0.08em' : '0.02em',
              lineHeight: 1.15,
              color: NIGHT.white,
              textShadow: '0 0 26px rgba(167,139,250,0.45), 0 2px 14px rgba(0,0,0,0.8)'
            }}
          >
            {levelEvent ? String(levelEvent.levelName || '').toUpperCase() : celebrations[0]?.headline || celebrations[0]?.title}
          </Typography>
          {levelEvent ? (
            <Typography sx={{ mt: 0.5, fontSize: 14, color: NIGHT.dim }}>{levelEvent.detail}</Typography>
          ) : (
            <Typography sx={{ mt: 0.5, fontSize: 14, color: NIGHT.dim }}>{celebrations[0]?.detail}</Typography>
          )}

          {(levelEvent ? others : others.slice(1)).length ? (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {(levelEvent ? others : others.slice(1)).map((c) => (
                <Box
                  key={c.key}
                  sx={{
                    borderRadius: 2.5,
                    px: 2,
                    py: 1,
                    bgcolor: 'rgba(16,11,30,0.72)',
                    border: '1px solid rgba(167,139,250,0.3)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NIGHT.white }}>
                    {c.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: NIGHT.dim }}>
                    {c.detail}
                  </Typography>
                </Box>
              ))}
              {celebrations.length > 5 ? (
                <Typography variant="caption" sx={{ color: NIGHT.dim }}>
                  +{celebrations.length - 5} more on your missions page
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          <Box sx={{ mt: 3 }}>
            <GoldCTA onClick={() => dismiss(true)} sx={{ px: 4.5, fontSize: 14 }}>
              SEE MY MISSIONS
            </GoldCTA>
          </Box>
          <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 1.5 }}>
            {shareable ? (
              <Button
                variant="text"
                sx={{ color: NIGHT.violetSoft, fontWeight: 700 }}
                onClick={() => downloadShareCard({ brandName, hostFirstName, ...shareable.share })}
              >
                Share to story
              </Button>
            ) : null}
            <Button variant="text" onClick={() => dismiss(false)} sx={{ color: 'rgba(245,243,255,0.6)' }}>
              Close
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
}
